import { useMemo } from 'react';
import { List } from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Button, Checkbox, ListRow, PopoverMenu, PopoverMenuItem, usePopoverMenu } from '@frontend/design-system';
import { useAudienceTray } from '../providers/audience-tray-provider';

type Props = {
  checked: boolean;
  disabled?: boolean;
  list: List;
  onCheck: () => void;
  startDeleteList: (list: List) => void;
  startEditList: (list: List) => Promise<void>;
  startRenameList: (list: List) => void;
};

export const ListItem = ({
  checked,
  disabled = false,
  list,
  onCheck,
  startDeleteList,
  startEditList,
  startRenameList,
}: Props) => {
  const { t } = useTranslation('bulk-messaging');
  const { trackingIdPrefix } = useAudienceTray();
  const { getTriggerProps, getMenuProps, getItemProps } = usePopoverMenu<HTMLButtonElement | HTMLAnchorElement>();

  const borderStyles = useMemo(() => {
    if (disabled) {
      return { border: `1px solid ${theme.colors.neutral20}` };
    }
    return {
      border: `1px solid ${checked ? theme.colors.primary50 : theme.colors.neutral10}`,
      ':hover': { borderColor: theme.colors.primary50 },
    };
  }, [disabled, checked]);

  return (
    <ListRow
      as='label'
      css={[
        borderStyles,
        {
          padding: theme.spacing(1, 2),
          marginBottom: theme.spacing(1),
          borderRadius: theme.borderRadius.medium,
          cursor: 'pointer',
          ':hover': { backgroundColor: 'transparent' },
        },
      ]}
      data-trackingid={`${trackingIdPrefix}-contacts-list-item`}
    >
      <ListRow.Lead>
        {/* @ts-expect-error purposefully omitting all of the checkbox's props */}
        <Checkbox value={checked} onChange={onCheck} name={list.listId ?? ''} disabled={disabled} />
      </ListRow.Lead>
      <ListRow.Content>
        <ListRow.Content.Title css={disabled && { color: theme.colors.neutral20 }}>
          {list.name ? list.name : t('Untitled List')}
        </ListRow.Content.Title>
        <ListRow.Content.Subtitle css={disabled && { color: theme.colors.neutral20 }}>
          {t('{{count}} Contacts', { count: list.recipientCount ?? 0 })}
        </ListRow.Content.Subtitle>
      </ListRow.Content>
      <ListRow.Trail>
        {disabled ? (
          <Icon name='more' color='disabled' />
        ) : (
          <>
            <Button
              size='large'
              iconName='more'
              css={{ svg: { color: theme.colors.neutral90 } }}
              variant='tertiary'
              hoverLabel={t('Actions')}
              {...getTriggerProps()}
            />
            <PopoverMenu {...getMenuProps()}>
              <PopoverMenuItem
                Icon={() => <Icon name='user-management' />}
                {...getItemProps({
                  index: 0,
                  onClick: () => startEditList(list),
                })}
                trackingId={`${trackingIdPrefix}-contact-list-edit-btn`}
              >
                {t('Edit List')}
              </PopoverMenuItem>
              <PopoverMenuItem
                Icon={() => <Icon name='edit' />}
                {...getItemProps({
                  index: 0,
                  onClick: () => startRenameList(list),
                })}
                trackingId={`${trackingIdPrefix}-contact-list-rename-btn`}
              >
                {t('Rename List')}
              </PopoverMenuItem>
              <PopoverMenuItem
                Icon={() => <Icon name='trash' />}
                {...getItemProps({
                  index: 1,
                  onClick: () => startDeleteList(list),
                })}
                trackingId={`${trackingIdPrefix}-contact-list-delete-btn`}
              >
                {t('Delete List')}
              </PopoverMenuItem>
            </PopoverMenu>
          </>
        )}
      </ListRow.Trail>
    </ListRow>
  );
};
